/**
 * Planner data. Two collections of its own — `plan_entries` and a one-row
 * `plan_settings` — and nothing else: see the note at the top of lib/planner.ts
 * for why the planner never reaches into projects, clients or time.
 *
 * Only the inputs are stored. Every figure on the Planner page is recomputed
 * from these on each render.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { pb } from "@/lib/pocketbase";
import type { PlanEntry, PlanSettings } from "@/lib/planner";

const userId = (): string => (pb.authStore.model as { id?: string } | null)?.id ?? "";

type Row<T> = T & { id: string };

/** A json field comes back null when unset; the engine wants an array. */
function clean(r: PlanEntry): PlanEntry {
  return { ...r, segments: Array.isArray(r.segments) ? r.segments : [{ from: r.start }] };
}

export function usePlanEntries() {
  return useQuery({
    queryKey: ["plan_entries"],
    queryFn: async () =>
      (await pb.collection("plan_entries").getFullList<PlanEntry>({ sort: "position,created" })).map(clean),
  });
}

export const EMPTY_SETTINGS: PlanSettings = { capacity: [], goals: {} };

export function usePlanSettings() {
  return useQuery({
    queryKey: ["plan_settings"],
    queryFn: async (): Promise<Row<PlanSettings> | null> => {
      const rows = await pb.collection("plan_settings").getFullList<Row<PlanSettings>>({ perPage: 1 });
      const r = rows[0];
      if (!r) return null;
      return { ...r, capacity: Array.isArray(r.capacity) ? r.capacity : [], goals: r.goals && typeof r.goals === "object" ? r.goals : {} };
    },
  });
}

export function usePlanMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["plan_entries"] });

  const saveEntry = useMutation({
    mutationFn: (entry: Partial<PlanEntry> & { id?: string }) => {
      const { id, ...data } = entry;
      return id
        ? pb.collection("plan_entries").update<PlanEntry>(id, data)
        : pb.collection("plan_entries").create<PlanEntry>({ ...data, user: userId() });
    },
    onSuccess: invalidate,
  });

  const deleteEntry = useMutation({
    mutationFn: (id: string) => pb.collection("plan_entries").delete(id),
    onSuccess: invalidate,
  });

  /** Store a manual order: writes only the entries whose position changed. */
  const reorder = useMutation({
    mutationFn: async (ordered: PlanEntry[]) => {
      for (const [i, e] of ordered.entries()) {
        if (e.position !== i) await pb.collection("plan_entries").update(e.id, { position: i });
      }
    },
    // Show the new order at once; an arrow that waits on the network feels
    // broken. The refetch afterwards corrects it if a write failed.
    onMutate: (ordered) => {
      qc.setQueryData<PlanEntry[]>(["plan_entries"], ordered.map((e, i) => ({ ...e, position: i })));
    },
    onSettled: invalidate,
  });

  /** Creates the settings row on first save; there is only ever one. */
  const saveSettings = useMutation({
    mutationFn: async (settings: PlanSettings) => {
      const rows = await pb.collection("plan_settings").getFullList<Row<PlanSettings>>({ perPage: 1 });
      return rows[0]
        ? pb.collection("plan_settings").update(rows[0].id, settings)
        : pb.collection("plan_settings").create({ ...settings, user: userId() });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["plan_settings"] }),
  });

  return { saveEntry, deleteEntry, reorder, saveSettings };
}
